import React, { useEffect, useState, Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/browser'
import { setAppLocales, essentialLocales, i18n } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { HelperMode } from '@fs/zion-helper-mode'
import { AppLayout, colors } from '@fs/zion-ui'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { getPreference, setPreference } from '@fs/zion-preferences'
import { NoticeLoading } from '@fs/zion-icon'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import utils from './lib/utils'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      ignoreErrors: [
        /.*-extension:\/\/.*/, // Browser extensions (chrome-extension, moz-extension, etc.)
        /.*Extension context invalidated.*/, // Chrome Extension error that is not caught by the first
        /.*QuotaExceededError.*/, // Non-critical localstorage errors
        /.*StorageQuotaExceeded.*/, // Non-critical localstorage errors
        /.*status.*401.*/, // Happens when a user session times out
      ],
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// Add any app logic here as flags, languages and translations are now configured and available
const FrontierWrapper = () => {
  setAppLocales(essentialLocales)

  const [t] = useTranslation()

  const [showFooter, setShowFooter] = useState({})
  const [isSourceBox, setIsSourceBox] = useState(false)
  const [open, setOpen] = useState(false)
  const footerProp = !showFooter ? { footer: { hidden: true } } : {}
  const fullScreenProp = !showFooter ? { fullscreen: true } : {}

  useEffect(() => {
    getPreference('sourcebox.alert-banner.dismissed')
      .then((pref) => {
        setOpen(isSourceBox && !pref)
      })
      .catch(console.error)
  }, [isSourceBox])

  const alertBanner =
    i18n.language === 'en'
      ? {
          type: 'info',
          message: t('source-box.learn-more-info'),
          confirmText: t('source-box.learn-more'),
          dismissText: t('source-box.dismiss'),
          onConfirm: () => {
            const learnMoreUrl = utils.getTranslatedHelpUrl()
            window.open(learnMoreUrl, '_blank')
          },
          onDismiss: (closeBanner) => {
            setPreference('sourcebox.alert-banner.dismissed', true)
            closeBanner()
          },
          open,
          setOpen,
        }
      : null

  return (
    <Router basename={base}>
      <Root analytics {...footerProp} {...fullScreenProp} alertBannerProps={alertBanner}>
        <AppLayout fullWidth backgroundColor={colors.gray02}>
          <HelperMode />
          <App setShowFooter={setShowFooter} setIsSourceBox={setIsSourceBox} />
        </AppLayout>
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </React.StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
